import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  House, 
  ForkKnife, 
  Barbell, 
  ChartLineUp, 
  User,
  Globe
} from "@phosphor-icons/react";

import Onboarding from "./pages/Onboarding";
import LandingPage from "./pages/LandingPage";
import CheckoutPage from "./pages/CheckoutPage";
import Dashboard from "./pages/Dashboard";
import Nutrition from "./pages/Nutrition";
import Workouts from "./pages/Workouts";
import Progress from "./pages/Progress";
import Profile from "./pages/Profile";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Generate or retrieve user ID from localStorage
const getUserId = () => {
  let userId = localStorage.getItem('myfitbody_user_id');
  if (!userId) {
    userId = 'user-' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('myfitbody_user_id', userId);
  }
  return userId;
};

export const USER_ID = getUserId();

const Navigation = () => {
  const location = useLocation();
  const { language, setLanguage } = useLanguage();
  const labels = {
    dashboard: language === "fr" ? "Tableau de bord" : "Dashboard",
    nutrition: language === "fr" ? "Nutrition" : "Nutrition",
    workouts: language === "fr" ? "Entraînements" : "Workouts",
    progress: language === "fr" ? "Progression" : "Progress",
    profile: language === "fr" ? "Ton profil" : "Your profile",
    buyNow: language === "fr" ? "Acheter maintenant" : "Buy now",
  };
  
  const navItems = [
    { path: "/dashboard", icon: House, label: labels.dashboard },
    { path: "/nutrition", icon: ForkKnife, label: labels.nutrition },
    { path: "/workouts", icon: Barbell, label: labels.workouts },
    { path: "/progress", icon: ChartLineUp, label: labels.progress },
    { path: "/profile", icon: User, label: labels.profile },
  ];

  // Hide navigation on onboarding
  if (location.pathname === "/" || location.pathname === "/onboarding") {
    return null;
  }

  return (
    <>
      {/* Desktop Header */}
      <header className="hidden md:grid fixed top-0 left-0 right-0 h-16 border-b border-black/5 bg-white/75 backdrop-blur-xl items-center grid-cols-[auto_1fr_auto] px-8 z-50 gap-5">
        <nav className="flex items-center gap-1 justify-self-start">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/é/g, 'e').replace(/î/g, 'i')}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200 ${
                  isActive
                    ? "bg-[#FF6B35] text-white font-semibold shadow-[0_12px_20px_rgba(255,107,53,0.22)]"
                    : "text-gray-500 hover:text-[#0A0A0A] hover:bg-[#F4F4F4]"
                }`}
              >
                <Icon size={20} weight={isActive ? "fill" : "regular"} />
                <span className="text-sm">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 justify-self-center">
          <div className="w-10 h-10 bg-[#0A0A0A] rounded-xl flex items-center justify-center shadow-[0_10px_25px_rgba(0,0,0,0.14)]">
            <span className="text-white font-bold text-lg">M</span>
          </div>
          <div>
            <span className="font-display text-2xl uppercase leading-none text-[#0A0A0A]">myfitbody.fr</span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-gray-400 block mt-0.5">nutrition · entraînement · suivi</span>
          </div>
        </div>

        <div className="flex items-center gap-3 justify-self-end">
          <div className="flex items-center gap-1 rounded-full border border-black/6 bg-white px-1 py-1" data-testid="nav-language-toggle">
            <Globe size={16} className="ml-2 text-gray-400" />
            <button className={`px-3 py-1.5 rounded-full text-xs font-bold ${language === 'fr' ? 'bg-[#0A0A0A] text-white' : 'text-gray-500'}`} data-testid="nav-language-fr" onClick={() => setLanguage('fr')} type="button">FR</button>
            <button className={`px-3 py-1.5 rounded-full text-xs font-bold ${language === 'en' ? 'bg-[#0A0A0A] text-white' : 'text-gray-500'}`} data-testid="nav-language-en" onClick={() => setLanguage('en')} type="button">EN</button>
          </div>
          <NavLink className="landing-primary-button !min-h-[2.75rem] !px-4" data-testid="nav-buy-now-button" to="/buy-now">{labels.buyNow}</NavLink>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/92 border-t border-black/5 z-50 safe-area-pb backdrop-blur-xl">
        <div className="flex justify-around py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-mobile-${item.label.toLowerCase().replace(/é/g, 'e').replace(/î/g, 'i')}`}
                className={`flex flex-col items-center py-2 px-3 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? "text-[#FF6B35]" 
                    : "text-gray-400"
                }`}
              >
                <Icon size={22} weight={isActive ? "fill" : "regular"} />
                <span className="text-xs mt-1">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </>
  );
};

function App() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initProfile = async () => {
      try {
        const response = await axios.post(`${API}/profile`, {
          id: USER_ID,
          name: "",
          gender: "male",
          age_range: "25-35",
          objective: "tone",
          target_zones: ["Pectoraux", "Abdominaux"],
          fitness_level: "intermediate",
          weight: 75,
          height: 175,
          goal_weight: 72,
          daily_calorie_goal: 2000,
          daily_protein_goal: 150,
          daily_carbs_goal: 250,
          daily_fat_goal: 65,
          onboarding_complete: true,
        });
        setProfile(response.data);
      } catch (error) {
        console.error("Error initializing profile:", error);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };
    initProfile();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-charcoal rounded-xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">M</span>
          </div>
          <p className="text-gray-500">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#0A0A0A]">
      <LanguageProvider>
        <BrowserRouter>
          <Navigation />
          <main className="md:pt-16 pb-20 md:pb-0">
            <Routes>
              <Route path="/" element={<LandingPage profile={profile} />} />
              <Route path="/onboarding" element={<Onboarding setProfile={setProfile} />} />
              <Route path="/dashboard" element={<Dashboard profile={profile} />} />
              <Route path="/nutrition" element={<Nutrition profile={profile} />} />
              <Route path="/workouts" element={<Workouts profile={profile} />} />
              <Route path="/progress" element={<Progress profile={profile} />} />
              <Route path="/profile" element={<Profile profile={profile} setProfile={setProfile} />} />
              <Route path="/buy-now" element={<CheckoutPage />} />
            </Routes>
          </main>
          <Toaster />
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
